import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { customizationsAPI, formatCustomizationMoney } from '../../api/customizationsAPI';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { trackPaintingCustomization } from '../../utils/analytics';

const Page = styled.main`max-width: 680px; margin: 0 auto; padding: 2rem 1rem 5rem;`;
const Back = styled(Link)`color: #1b4332; display: inline-block; font-weight: 700; margin-bottom: 1.25rem;`;
const Title = styled.h1`color: var(--text-primary); font-size: 2rem; margin: 0 0 1.25rem;`;
const Summary = styled.section`background: #e8f3ec; border-radius: 0.75rem; margin-bottom: 1.5rem; padding: 1rem;`;
const SummaryTitle = styled.h2`color: #1b4332; font-size: 1rem; margin: 0 0 0.4rem;`;
const Group = styled.div`margin-top: 1.25rem;`;
const Label = styled.label`color: var(--text-primary); display: block; font-weight: 800; margin-bottom: 0.45rem;`;
const Input = styled.input`
  border: 1.5px solid ${(p) => p.$error ? '#dc2626' : 'var(--border-color, #d1d5db)'};
  border-radius: 0.55rem; box-sizing: border-box; font: inherit; padding: 0.8rem; width: 100%;
`;
const Textarea = styled.textarea`
  border: 1.5px solid var(--border-color, #d1d5db); border-radius: 0.55rem;
  box-sizing: border-box; font: inherit; min-height: 120px; padding: 0.8rem; resize: vertical; width: 100%;
`;
const Helper = styled.p`color: var(--text-secondary); font-size: 0.8rem; line-height: 1.45; margin: 0.4rem 0 0;`;
const Error = styled.p`color: #b91c1c; font-size: 0.8rem; margin: 0.35rem 0 0;`;
const Note = styled.p`color: var(--text-secondary); line-height: 1.5; margin: 1.5rem 0;`;
const Actions = styled.div`display: flex; gap: 0.75rem;`;
const Primary = styled.button`
  background: #1b4332; border: 0; border-radius: 0.55rem; color: #fff; cursor: pointer;
  font-weight: 800; padding: 0.85rem 1.2rem; &:disabled { cursor: not-allowed; opacity: 0.55; }
`;
const Cancel = styled(Link)`color: var(--text-secondary); font-weight: 700; padding: 0.85rem; text-decoration: none;`;

const ArtistCustomizationQuotePage = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [price, setPrice] = useState('');
  const [deliveryDays, setDeliveryDays] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    customizationsAPI.getArtistRequest(requestId)
      .then((data) => {
        setRequest(data);
        const quote = data.active_quote || data.quotes?.[0];
        if (quote) {
          setPrice(String(Number(quote.amount_cents) / 100));
          setDeliveryDays(String(quote.delivery_days));
          setMessage(quote.message || '');
        }
      })
      .catch(() => toast.error('Something went wrong. Try again.'))
      .finally(() => setLoading(false));
  }, [requestId]);

  const submit = async (event) => {
    event.preventDefault();
    const amount = Number(price);
    const days = Number(deliveryDays);
    const nextErrors = {};
    if (!Number.isFinite(amount) || amount <= 0) nextErrors.price = 'Enter a painting price greater than 0.';
    if (!Number.isInteger(days) || days <= 0) nextErrors.deliveryDays = 'Enter an estimated delivery time greater than 0 days.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSending(true);
    try {
      const quote = await customizationsAPI.sendArtistQuote(requestId, {
        amount_cents: Math.round(amount * 100),
        delivery_days: days,
        message: message.trim(),
      });
      trackPaintingCustomization('painting_quote_sent', {
        request_id: Number(requestId),
        shop_id: request.selected_shop?.id || request.selected_shop,
        quote_id: quote.id,
        amount_cents: quote.amount_cents,
        delivery_days: quote.delivery_days,
        is_revision: request.state === 'quoted',
        state: 'quoted',
      });
      toast.success(request.state === 'quoted' ? 'Revised quote sent.' : 'Quote sent.');
      navigate(`/artist/customization-requests/${requestId}`, { replace: true });
    } catch (err) {
      if ([404, 409].includes(err.response?.status)) {
        toast.error('This request has been updated and can no longer be quoted.');
        navigate(`/artist/customization-requests/${requestId}`, { replace: true });
      } else {
        toast.error(err.response?.data?.error || 'Something went wrong. Try again.');
      }
    } finally {
      setSending(false);
    }
  };

  if (loading) return <LoadingSpinner text="Loading quote form…" />;
  if (!request) return <Page><Title>This request isn’t available.</Title><Back to="/artist/customization-requests">Back to requests</Back></Page>;
  const revising = request.state === 'quoted';

  return (
    <Page>
      <Back to={`/artist/customization-requests/${requestId}`}>← Back to request</Back>
      <Title>{revising ? 'Revise quote' : 'Send a quote'}</Title>
      <Summary>
        <SummaryTitle>Painting brief</SummaryTitle>
        <div>
          {request.painting_brief?.width_cm} × {request.painting_brief?.height_cm} cm ·{' '}
          {formatCustomizationMoney(request.budget_min_cents)} – {formatCustomizationMoney(request.budget_max_cents)}
        </div>
        <Back style={{ margin: '0.65rem 0 0' }} to={`/artist/customization-requests/${requestId}`}>View full brief</Back>
      </Summary>
      <form onSubmit={submit}>
        <Group>
          <Label htmlFor="quote-price">Painting price</Label>
          <Input id="quote-price" type="number" min="0.01" step="0.01" value={price} $error={errors.price} onChange={(e) => setPrice(e.target.value)} disabled={sending} />
          {errors.price ? <Error>{errors.price}</Error> : null}
          <Helper>Enter the price for creating this painting. Your gallery minimum is a guide, so this quote may be lower or higher.</Helper>
        </Group>
        <Group>
          <Label htmlFor="delivery-days">Estimated delivery (days)</Label>
          <Input id="delivery-days" type="number" min="1" step="1" value={deliveryDays} $error={errors.deliveryDays} onChange={(e) => setDeliveryDays(e.target.value)} disabled={sending} />
          {errors.deliveryDays ? <Error>{errors.deliveryDays}</Error> : null}
          <Helper>Estimate the number of days after the order is placed.</Helper>
        </Group>
        <Group>
          <Label htmlFor="quote-message">Message to buyer (optional)</Label>
          <Textarea id="quote-message" value={message} onChange={(e) => setMessage(e.target.value)} disabled={sending} placeholder="Explain what the price includes or clarify your approach…" />
        </Group>
        <Note>The buyer will see the painting price and delivery estimate before accepting. Existing checkout fees, if any, are shown at checkout.</Note>
        <Actions>
          <Primary type="submit" disabled={sending}>{sending ? 'Sending quote…' : revising ? 'Send revised quote' : 'Send quote'}</Primary>
          <Cancel to={`/artist/customization-requests/${requestId}`}>Cancel</Cancel>
        </Actions>
      </form>
    </Page>
  );
};

export default ArtistCustomizationQuotePage;
